import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from 'react-bootstrap';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './utStyle.css';
import config from '../../config.json';

const apiUrl = config.apiUrl;

const getStatusBadge = (status) => {
    let variant;
    switch (status) {
        case 'Pending':
            variant = 'primary';
            break;
        case 'Approved':
            variant = 'success';
            break;
        case 'Rejected':
            variant = 'danger';
            break;
        default:
            variant = 'secondary';
    }
    return <Badge bg={variant}>{status}</Badge>;
};

const Statuses = {
    Pending: 0,
    Approved: 1,
    Rejected: 2
};

const UndertimeList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [currentEmployeeId, setCurrentEmployeeId] = useState(null);
    const [undertimeData, setUndertimeData] = useState([]);
    const [selectedUndertimeIds, setSelectedUndertimeIds] = useState([]);
    const [dropdownStates, setDropdownStates] = useState({});
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearchInput, setDebouncedSearchInput] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingAction, setPendingAction] = useState('');
    const navigate = useNavigate();
    const selectAllRef = useRef(null);
    const roles = JSON.parse(localStorage.getItem('Roles'));

    const [statusFilters, setStatusFilters] = useState({
        Pending: true,
        Approved: true,
        Rejected: true,
    });

    const userAccess = {
        hasView: roles?.includes('View') || false,
        hasAdmin: roles?.includes('Admin') || false,
        hasApprover: roles?.includes('Approver') || false,
        hasStandard: roles?.includes('Standard') || false,
    };

    const { hasView, hasAdmin, hasStandard, hasApprover } = userAccess;

    const [activeTab, setActiveTab] = useState('');

    useEffect(() => {
        if (hasStandard || hasAdmin) {
            setActiveTab('transactions');
        } else if (hasApprover) {
            setActiveTab('forApproval');
        } else {
            setActiveTab('transactions');
        }
    }, []);

    const toggleDropdown = (itemId) => {
        setDropdownStates((prevState) => ({
            ...prevState,
            [itemId]: !prevState[itemId],
        }));
    };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setStatusFilters({
      ...statusFilters,
      [name]: checked,
    });
  };

  const fetchEmployeeNameList = async () => {
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${apiUrl}/Employee/Namelist`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const result = await response.json();
                const employee = result.Employee && result.Employee.length > 0 ? result.Employee[0] : null;
                setCurrentEmployeeId(employee ? employee.EmployeeID : null);
            }
        } catch (error) {
            setCurrentEmployeeId(null);
        }
    };

    const handleUndertimeSelection = (id, checked) => {
        setSelectedUndertimeIds((prevIds) => {
            if (checked) {
                return prevIds.includes(id) ? prevIds : [...prevIds, id];
            }

            return prevIds.filter((selectedId) => selectedId !== id);
        });
    };

    const fetchUndertimeData = async (searchTerm) => {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        const statusQueryString = Object.keys(statusFilters)
            .filter((key) => statusFilters[key])
            .map((key) => `statuses=${Statuses[key]}`)
            .join('&');
        const employeeQueryString = currentEmployeeId ? `&empID=${currentEmployeeId}` : '';
        const apiUrlEndpoint = `${apiUrl}/Undertime?searchTerm=${searchTerm}&${statusQueryString}${employeeQueryString}&pageNumber=${currentPage}&pageSize=${itemsPerPage}`;

        try {
            const response = await fetch(apiUrlEndpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const result = await response.json();
                setUndertimeData(result.Data || []);
                setTotalPages(result.TotalCount ? Math.ceil(result.TotalCount / itemsPerPage) : 0);
                setSelectedUndertimeIds([]);
            } else {
                setErrorMessage('Unable to load undertime records.');
                setSuccessMessage('');
            }
        } catch (error) {
            setErrorMessage('Unable to load undertime records.');
            setSuccessMessage('');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchUndertimeForApprovalData = async (searchTerm) => {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        const apiUrlEndpoint = `${apiUrl}/Undertime/ForApproval?searchTerm=${searchTerm}&pageNumber=${currentPage}&pageSize=${itemsPerPage}`;

        try {
            const response = await fetch(apiUrlEndpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const result = await response.json();
                setUndertimeData(result.Data || []);
                setTotalPages(result.TotalCount ? Math.ceil(result.TotalCount / itemsPerPage) : 0);
                setSelectedUndertimeIds([]);
            } else {
                setErrorMessage('Unable to load undertime records for approval.');
                setSuccessMessage('');
            }
        } catch (error) {
            setErrorMessage('Unable to load undertime records for approval.');
            setSuccessMessage('');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchInput(searchInput);
        }, 1);

        return () => clearTimeout(timeoutId);
    }, [searchInput]);

    useEffect(() => {
        if (hasStandard || !hasApprover) {
            fetchEmployeeNameList();
        }
    }, []);

    useEffect(() => {
        if (!activeTab) return;
        if (activeTab === 'transactions') {
            if (!hasApprover && !currentEmployeeId) {
                return;
            }
            fetchUndertimeData(debouncedSearchInput);
        } else if (activeTab === 'forApproval') {
            fetchUndertimeForApprovalData(debouncedSearchInput);
        }
    }, [activeTab, debouncedSearchInput, statusFilters, currentPage, itemsPerPage, currentEmployeeId]);

    const currentItems = undertimeData;
    const selectedCurrentItemsCount = currentItems.filter((item) => selectedUndertimeIds.includes(item.ID)).length;
    const areAllCurrentItemsSelected = currentItems.length > 1 && selectedCurrentItemsCount === currentItems.length;
    const hasMeaningfulBulkSelection = selectedCurrentItemsCount >= 2;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = hasMeaningfulBulkSelection && !areAllCurrentItemsSelected;
        }
    }, [areAllCurrentItemsSelected, hasMeaningfulBulkSelection]);

    const updateUndertimeStatus = async (ids, status) => {
        const token = localStorage.getItem('token');
        const actionLabel = status === 'Approved' ? 'approval' : 'rejection';
        const endpointAction = status === 'Approved' ? 'Approve' : 'Reject';
        setIsSubmitting(true);
        setPendingAction(status);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const responses = await Promise.all(
                ids.map(async (id) => {
                    const undertimeRecord = undertimeData.find((item) => item.ID === id);
                    const response = await fetch(`${apiUrl}/Undertime/${endpointAction}/${id}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            Remarks: undertimeRecord?.Remarks ?? '',
                        }),
                    });

                    let data = null;
                    try {
                        data = await response.json();
                    } catch (parseError) {
                        data = null;
                    }

                    return { response, data };
                })
            );

            const failedResponse = responses.find(({ response }) => !response.ok);
            if (failedResponse) {
                setErrorMessage(failedResponse.data?.Message || `Unable to complete the ${actionLabel} request.`);
                return;
            }

            setSuccessMessage(status === 'Approved' ? 'Successfully Approved' : 'Successfully Rejected');
            setSelectedUndertimeIds([]);
            setTimeout(async () => {
                setSuccessMessage('');
                if (activeTab === 'transactions') {
                    await fetchUndertimeData(debouncedSearchInput);
                } else {
                    await fetchUndertimeForApprovalData(debouncedSearchInput);
                }
            }, 3000);
        } catch (error) {
            setErrorMessage(`Unable to complete the ${actionLabel} request.`);
        } finally {
            setIsSubmitting(false);
            setPendingAction('');
        }
    };

    const handleUndertimeApproval = async (status) => {
        if (selectedUndertimeIds.length === 0) {
            return;
        }

        await updateUndertimeStatus(selectedUndertimeIds, status);
    };

    const handleSelectAllUndertime = () => {
        const currentItemIds = currentItems.map((item) => item.ID);
        setSelectedUndertimeIds((prevIds) => {
            const hasAnySelectedCurrentItems = currentItemIds.some((id) => prevIds.includes(id));

            if (hasAnySelectedCurrentItems) {
                return prevIds.filter((id) => !currentItemIds.includes(id));
            }

            const nextIds = [...prevIds];
            currentItemIds.forEach((id) => {
                if (!nextIds.includes(id)) {
                    nextIds.push(id);
                }
            });

            return nextIds;
        });
    };

    const handleTabChange = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            setCurrentPage(1);
            setSelectedUndertimeIds([]);
        }
    };

    return (
        <div>
            {userAccess.hasView ? (
                <>
                    <h3>Undertime Listing</h3>

                    {}
                    <ul className="nav nav-tabs mb-3">
                        {(hasStandard || hasAdmin) && (
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === 'transactions' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('transactions')}
                                >
                                    My Transaction
                                </button>
                            </li>
                        )}
                        {hasApprover && (
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${activeTab === 'forApproval' ? 'active' : ''}`}
                                    onClick={() => handleTabChange('forApproval')}
                                >
                                    For Approval
                                </button>
                            </li>
                        )}
                    </ul>

                    {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                    {(userAccess.hasAdmin || userAccess.hasStandard) && activeTab === 'transactions' && (
                        <Link to='/undertime-create' className='btn btn-primary float-end mb-2'>Add undertime</Link>
                    )}

                    <div className='d-flex align-items-start mb-2'>
                        <div className='custom-width'>
                            <input
                                type='text'
                                placeholder='Search..'
                                className='form-control'
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>

                        {activeTab === 'transactions' && (
                            <div className="accordion ms-2" id="accordionFilter">
                                <div className="accordion-item">
                                    <h2 className="accordion-header" id="headingFilter">
                                        <button
                                            className="accordion-button"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target="#collapseFilter"
                                            aria-expanded="true"
                                            aria-controls="collapseFilter"
                                            style={{ height: '35px' }}
                                        >
                                            Status
                                        </button>
                                    </h2>
                                    <div
                                        id="collapseFilter"
                                        className="accordion-collapse collapse position-absolute"
                                        aria-labelledby="headingFilter"
                                        data-bs-parent="#accordionFilter"
                                    >
                                        <div className="accordion-body">
                                            <div className="accordion" id="nestedAccordion">
                                                <div className="accordion-item border-2">
                                                    <div
                                                        id="collapseStatusFilter"
                                                        className="accordion-collapse collapse show"
                                                        aria-labelledby="headingStatusFilter"
                                                        data-bs-parent="#nestedAccordion"
                                                    >
                                                        <div className="accordion-body">
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id="pendingCheckbox"
                                                                    name="Pending"
                                                                    checked={statusFilters.Pending}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <label className="form-check-label" htmlFor="pendingCheckbox">
                                                                    Pending
                                                                </label>
                                                            </div>
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id="approvedCheckbox"
                                                                    name="Approved"
                                                                    checked={statusFilters.Approved}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <label className="form-check-label" htmlFor="approvedCheckbox">
                                                                    Approved
                                                                </label>
                                                            </div>
                                                            <div className="form-check">
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-check-input"
                                                                    id="rejectedCheckbox"
                                                                    name="Rejected"
                                                                    checked={statusFilters.Rejected}
                                                                    onChange={handleCheckboxChange}
                                                                />
                                                                <label className="form-check-label" htmlFor="rejectedCheckbox">
                                                                    Rejected
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'forApproval' && selectedUndertimeIds.length > 0 && (
                            <>
                                <button
                                    type='button'
                                    className='btn btn-success ms-2'
                                    disabled={selectedUndertimeIds.length === 0 || isSubmitting}
                                    onClick={() => handleUndertimeApproval('Approved')}
                                >
                                    {isSubmitting && pendingAction === 'Approved' ? 'Approving...' : 'Approve'}
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-danger ms-2'
                                    disabled={selectedUndertimeIds.length === 0 || isSubmitting}
                                    onClick={() => handleUndertimeApproval('Rejected')}
                                >
                                    {isSubmitting && pendingAction === 'Rejected' ? 'Rejecting...' : 'Reject'}
                                </button>
                            </>
                        )}
                    </div>

                    <table className='custom-table'>
                        <thead>
                            <tr>
                                <th className='border-end-0'></th>
                                {activeTab === 'forApproval' && (
                                    <th className='border-start-0 border-end-0 text-center'>
                                        <input
                                            ref={selectAllRef}
                                            type='checkbox'
                                            className='form-check-input'
                                            checked={areAllCurrentItemsSelected}
                                            onChange={handleSelectAllUndertime}
                                        />
                                    </th>
                                )}
                                <th className='border-start-0 border-end-0'>Transaction ID</th>
                                <th className='border-start-0 border-end-0'>Transaction Date</th>
                                <th className='border-start-0 border-end-0'>Employee</th>
                                <th className='border-start-0 border-end-0'>Date Start</th>
                                <th className='border-start-0 border-end-0'>Date End</th>
                                <th className='border-start-0 border-end-0'>Duration</th>
                                <th className='border-start-0'>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={activeTab === 'forApproval' ? 9 : 8}>
                                        <div className="d-flex align-items-center">
                                            <div className="spinner-border me-2" aria-hidden="true"></div>
                                            <span role="status"> Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                undertimeData && undertimeData.length > 0 ? (
                                    undertimeData.map((item) => (
                                        <React.Fragment key={item.ID}>
                                            <tr>
                                                <td className="text-sm-center remove-space border-end-0" style={{ borderBottom: 'none' }}>
                                                    <div className="dropdown">
                                                        <button
                                                            className="btn"
                                                            type="button"
                                                            id={`dropdownMenuButton${item.ID}`}
                                                            data-bs-toggle="dropdown"
                                                            data-bs-auto-close="outside"
                                                            aria-haspopup="true"
                                                            aria-expanded={dropdownStates[item.ID]}
                                                            onClick={() => toggleDropdown(item.ID)}
                                                        >
                                                            <i className="fas fa-ellipsis"></i>
                                                        </button>
                                                        <div
                                                            className={`dropdown-menu ${dropdownStates[item.ID] ? 'show' : ''}`}
                                                            aria-labelledby={`dropdownMenuButton${item.ID}`}
                                                        >
                                                            {activeTab === 'transactions' && (userAccess.hasStandard || userAccess.hasAdmin) && (
                                                                <Link to={`/undertime-update/${item.ID}`}
                                                                    className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                    Update
                                                                </Link>
                                                            )}
                                                            {(userAccess.hasView || userAccess.hasAdmin) && (
                                                                <Link to={`/undertime-view/${item.ID}`} className="dropdown-item">
                                                                    View
                                                                </Link>
                                                            )}
                                                            {activeTab === 'transactions' && (userAccess.hasStandard || userAccess.hasAdmin) && (
                                                                <Link to={`/undertime-delete/${item.ID}`}
                                                                    className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                    Delete
                                                                </Link>
                                                            )}
                                                            {activeTab === 'forApproval' && (userAccess.hasApprover || userAccess.hasAdmin) && (
                                                                <Link to={`/undertime-approve/${item.ID}`}
                                                                    className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                    Approve
                                                                </Link>
                                                            )}
                                                            {activeTab === 'forApproval' && (userAccess.hasApprover || userAccess.hasAdmin) && (
                                                                <Link to={`/undertime-reject/${item.ID}`}
                                                                    className={`dropdown-item ${item.Status === 'Approved' || item.Status === 'Rejected' ? 'disabled' : ''}`}>
                                                                    Reject
                                                                </Link>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                {activeTab === 'forApproval' && (
                                                    <td className='border-start-0 border-end-0 text-center align-middle' style={{ borderBottom: 'none' }}>
                                                        <input
                                                            type='checkbox'
                                                            className='form-check-input'
                                                            checked={selectedUndertimeIds.includes(item.ID)}
                                                            onChange={(event) => handleUndertimeSelection(item.ID, event.target.checked)}
                                                        />
                                                    </td>
                                                )}
                                                <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionID}</td>
                                                <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.TransactionDate}</td>
                                                <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Employee.Name}</td>
                                                <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.DateStart}</td>
                                                <td className='border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.DateEnd}</td>
                                                <td className='text-end border-start-0 border-end-0' style={{ borderBottom: 'none' }}>{item.Duration}</td>
                                                <td className='border-start-0' style={{ borderBottom: 'none' }}>{getStatusBadge(item.Status)}</td>
                                            </tr>
                                            <tr>
                                                <td colSpan={activeTab === 'forApproval' ? 9 : 8} style={{ borderTop: 'none', paddingLeft: activeTab === 'forApproval' ? '105px' : '65px', maxWidth: '50px', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                                    <strong>Remarks:</strong> {item.Remarks}
                                                </td>
                                            </tr>
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={activeTab === 'forApproval' ? 9 : 8}>No Record</td>
                                    </tr>
                                )
                            )}
                        </tbody>
                    </table>
                    <nav aria-label="Page navigation">
                        <ul className="pagination mt-3">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(1)}
                                    aria-label="First"
                                >
                                    <span aria-hidden="true">{"<<"}</span>
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} aria-label="Previous">
                                    <span aria-hidden="true">{"<"}</span>
                                </button>
                            </li>
                            {(() => {
                                const pageGroupSize = 5;
                                const currentGroup = Math.floor((currentPage - 1) / pageGroupSize);
                                const startPage = currentGroup * pageGroupSize + 1;
                                const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

                                const visiblePages = Array.from(
                                    { length: endPage - startPage + 1 },
                                    (_, index) => startPage + index
                                );

                                return visiblePages.map((page) => (
                                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(page)}>
                                            {page}
                                        </button>
                                    </li>
                                ));
                            })()}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} aria-label="Next">
                                    <span aria-hidden="true">{">"}</span>
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(totalPages)}
                                    aria-label="Last"
                                >
                                    <span aria-hidden="true">{">>"}</span>
                                </button>
                            </li>
                        </ul>
                    </nav>
                </>
            ) : (
                <div>
                    <h3>No access!</h3>
                    <button className="btn btn-primary" onClick={() => navigate(-1)}>
                        Go Back
                    </button>
                </div>
            )}
        </div>
    )
}

export default UndertimeList
